import { useOutletContext, Link } from "react-router-dom";
import { ChevronRight, Phone } from "lucide-react";
import { BISTRO_INFO, BISTRO_TEL } from "@/lib/bistroInfo";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[620px] overflow-hidden flex items-end">
        <div
          className="absolute inset-0 kenburns bg-cover bg-center"
          style={{ backgroundImage: "url('/images/interior.jpeg')" }}
          aria-hidden
        />
        <div className="absolute inset-x-0 bottom-0 h-[90%] bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/60 to-transparent" aria-hidden />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pb-20 md:pb-28 text-white [text-shadow:0_2px_18px_rgba(0,0,0,0.55)]">
          <div className="max-w-2xl fade-in">
            <span className="font-script text-2xl md:text-3xl text-[#D4A3A7] mb-4 block">Brasserie · Bar · Restaurant</span>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] mb-8">
              Le Bistrot<br /><span className="italic text-[#C64950]">de la Neuve</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-xl leading-relaxed mb-10">
              Une cuisine généreuse, à la fois moderne et traditionnelle, servie dans un cadre chaleureux en bois.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/menus"
                data-testid="hero-menus-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#A61C24] hover:bg-[#8B151E] text-white text-xs uppercase tracking-[0.25em] transition-colors"
              >
                Découvrir nos menus <ChevronRight size={16} />
              </Link>
              <a
                href={`tel:${BISTRO_TEL}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/40 hover:border-white text-white text-xs uppercase tracking-[0.25em] transition-colors"
              >
                <Phone size={14} /> Réserver au {BISTRO_INFO.telephone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
        <span className="font-script text-2xl text-[#A61C24]">Notre maison</span>
        <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mt-2 mb-8 leading-tight">
          Un bistrot <em className="text-[#A61C24]">chaleureux</em>,<br /> une table généreuse.
        </h2>
        <p className="text-[#1A1A1A]/75 leading-relaxed text-lg max-w-2xl mx-auto">
          Entre bois patiné, carreaux de ciment et banquettes en cuir, Le Bistrot de la Neuve cultive le charme d'une brasserie française à l'ancienne, avec la fraîcheur d'une cuisine d'aujourd'hui.
        </p>
        <p className="text-[#1A1A1A]/75 leading-relaxed text-lg max-w-2xl mx-auto mt-5">
          Nos plats du jour changent chaque semaine, nos formules de saison célèbrent les producteurs locaux, et notre bar vous accueille pour un verre à toute heure.
        </p>
      </section>

      {/* BLOCK 1 : L'intérieur */}
      <FeatureBlock
        eyebrow="La salle"
        title="L'intérieur du bistrot"
        subtitle="Séréna & Alexis"
        text="Dans une salle lumineuse habillée de bois, de carreaux de ciment rouges et de banquettes en cuir patiné, Séréna et Alexis vous accueillent comme à la maison. Plats du jour, formules express le midi, belle carte de saison : une cuisine française généreuse, travaillée à partir de produits frais."
        images={["/images/interieur.jpeg", "/images/interior.jpeg"]}
        imagesRight
        testId="block-interieur"
      />

      {/* BLOCK 2 : La terrasse */}
      <FeatureBlock
        eyebrow="Aux beaux jours"
        title="La terrasse"
        subtitle="Au calme, sous les guirlandes"
        text="Dès les premiers rayons de soleil, notre terrasse devient le coin préféré des habitués. Chaises bistrot tressées, tables en bois clair, guirlandes lumineuses et végétation généreuse : un écrin paisible pour déjeuner, prendre l'apéritif ou prolonger la soirée entre amis."
        images={["/images/terrace.jpeg", "/images/terasse2.jpeg"]}
        imagesRight={false}
        testId="block-terrasse"
        bgCream
      />

      {/* BLOCK 3 : Le bar */}
      <FeatureBlock
        eyebrow="À toute heure"
        title="Le bar"
        subtitle="Un verre, une gourmandise, un moment"
        text="Le bar du Bistrot de la Neuve, c'est l'endroit où l'on passe pour un café, un apéritif ou un verre en fin de journée, mais aussi pour une gourmandise à emporter ou à déguster sur place : glaces, gaufres, planches à partager et boissons fraîches. Une parenthèse conviviale, sans chichi."
        images={["/images/bar.jpeg", "/images/bar2.jpeg"]}
        imagesRight
        testId="block-bar"
      />
    </div>
  );
}

function FeatureBlock({ eyebrow, title, subtitle, text, images, imagesRight = true, testId, bgCream = false }) {
  const TextCol = (
    <div className="max-w-lg" data-testid={`${testId}-text`}>
      <span className="font-script text-2xl text-[#A61C24]">{eyebrow}</span>
      <h3 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mt-2 leading-tight">{title}</h3>
      {subtitle && (
        <p className="font-serif italic text-xl text-[#8B5A2B] mt-2">{subtitle}</p>
      )}
      <div className="w-12 h-px bg-[#A61C24] my-6" />
      <p className="text-[#1A1A1A]/75 leading-relaxed">{text}</p>
    </div>
  );

  const ImagesCol = (
    <div className="grid grid-cols-2 gap-4" data-testid={`${testId}-images`}>
      <img
        src={images[0]}
        alt=""
        loading="lazy"
        decoding="async"
        className="aspect-[3/4] object-cover w-full hover:scale-[1.02] transition-transform duration-500"
      />
      <img
        src={images[1]}
        alt=""
        loading="lazy"
        decoding="async"
        className="aspect-[3/4] object-cover w-full mt-12 hover:scale-[1.02] transition-transform duration-500"
      />
    </div>
  );

  return (
    <section
      className={`${bgCream ? "bg-[#E5E0D8]/40" : "bg-[#FDFBF7]"} py-24`}
      data-testid={testId}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
        {imagesRight ? (
          <>
            <div className="flex md:justify-start">{TextCol}</div>
            {ImagesCol}
          </>
        ) : (
          <>
            <div className="order-2 md:order-1">{ImagesCol}</div>
            <div className="order-1 md:order-2 flex md:justify-end">{TextCol}</div>
          </>
        )}
      </div>
    </section>
  );
}
